
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import { FaGoogle } from "react-icons/fa6";
import { SiAmazon } from "react-icons/si";
import { VscAzure } from "react-icons/vsc";

interface TenantCloudConfigureDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    cloudProvider: string;
    config: { name: string };
    cloudConfigData: any;
    setCloudConfigData: (data: any) => void;
    setIsConfigured: (value: boolean) => void;
    subUserReg: () => void
}

const cloudConfig: Record<string, { icon: any; name: string; fullName: string; label: string }> = {
    gcp: { icon: FaGoogle, name: "GCP", fullName: "Google Cloud Platform", label: "GCP" },
    aws: { icon: SiAmazon, name: "AWS", fullName: "Amazon Web Services", label: "AWS" },
    azure: { icon: VscAzure, name: "Azure", fullName: "Microsoft Azure", label: "Azure" },
};


export default function TenantCloudConfigureDialog({
    open,
    onOpenChange,
    cloudProvider,
    config,
    cloudConfigData,
    setCloudConfigData,
    setIsConfigured,
    subUserReg
}: TenantCloudConfigureDialogProps) {

    const configCloud = cloudConfig[cloudProvider];
    const CloudIcon = configCloud.icon;


    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold flex items-center gap-2">
                        <CloudIcon className="w-5 h-5 text-primary" />
                        Configure {config.name} Account
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Azure Fields */}
                    {cloudProvider === "azure" && (
                        <>
                            <div>
                                <Label>Display Name *</Label>
                                <Input
                                    placeholder="Enter Display Name"
                                    value={cloudConfigData.azureDisplayName}
                                    onChange={(e) =>
                                        setCloudConfigData({ ...cloudConfigData, azureDisplayName: e.target.value })
                                    }
                                />
                            </div>
                            <div>
                                <Label>Azure ID *</Label>
                                <Input
                                    placeholder="Enter Azure ID"
                                    value={cloudConfigData.azureId}
                                    onChange={(e) =>
                                        setCloudConfigData({ ...cloudConfigData, azureId: e.target.value })
                                    }
                                />
                            </div>
                        </>
                    )}

                    {/* GCP Fields */}
                    {cloudProvider === "gcp" && (
                        <>
                            <div>
                                <Label>GCP User Name *</Label>
                                <Input
                                    placeholder="Enter GCP User Name"
                                    value={cloudConfigData.gcpUserName}
                                    onChange={(e) =>
                                        setCloudConfigData({ ...cloudConfigData, gcpUserName: e.target.value })
                                    }
                                />
                            </div>
                            <div>
                                <Label>GCP Account Email *</Label>
                                <Input
                                    placeholder="Enter GCP Account Email"
                                    value={cloudConfigData.gcpAccountEmail}
                                    onChange={(e) =>
                                        setCloudConfigData({ ...cloudConfigData, gcpAccountEmail: e.target.value })
                                    }
                                />
                            </div>
                        </>
                    )}

                    {/* AWS Fields */}
                    {cloudProvider === "aws" && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <Label>AWS Credentials ({cloudConfigData.awsCredentials.length})</Label>
                                <Button
                                    size="sm"
                                    onClick={() =>
                                        setCloudConfigData({
                                            ...cloudConfigData,
                                            awsCredentials: [
                                                ...cloudConfigData.awsCredentials,
                                                { id: Date.now(), awsUserName: "", awsAccountARN: "" },
                                            ],
                                        })
                                    }
                                >
                                    + Add Credential
                                </Button>
                            </div>

                            {cloudConfigData.awsCredentials.length === 0 ? (
                                <p className="text-sm text-muted-foreground text-center py-4">
                                    No credentials added yet.
                                </p>
                            ) : (
                                cloudConfigData.awsCredentials.map((cred: any) => (
                                    <div key={cred.id} className="border rounded-lg p-3 space-y-2">
                                        <div>
                                            <Label>Username</Label>
                                            <Input
                                                value={cred.username}
                                                onChange={(e) =>
                                                    setCloudConfigData({
                                                        ...cloudConfigData,
                                                        awsCredentials: cloudConfigData.awsCredentials.map((c: any) =>
                                                            c.id === cred.id ? { ...c, awsUserName: e.target.value } : c
                                                        ),
                                                    })
                                                }
                                            />
                                        </div>
                                        <div>
                                            <Label>ARN</Label>
                                            <Input
                                                value={cred.arn}
                                                onChange={(e) =>
                                                    setCloudConfigData({
                                                        ...cloudConfigData,
                                                        awsCredentials: cloudConfigData.awsCredentials.map((c: any) =>
                                                            c.id === cred.id ? { ...c, awsAccountARN: e.target.value } : c
                                                        ),
                                                    })
                                                }
                                            />
                                        </div>
                                        <div className="flex justify-end">
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={() =>
                                                    setCloudConfigData({
                                                        ...cloudConfigData,
                                                        awsCredentials: cloudConfigData.awsCredentials.filter(
                                                            (c: any) => c.id !== cred.id
                                                        ),
                                                    })
                                                }
                                            >
                                                Remove
                                            </Button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-3">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            subUserReg?.()
                            setIsConfigured(true);
                            onOpenChange(false);
                        }}
                        disabled={
                            (cloudProvider === "azure" &&
                                (!cloudConfigData.azureDisplayName || !cloudConfigData.azureId)) ||
                            (cloudProvider === "gcp" &&
                                (!cloudConfigData.gcpUserName || !cloudConfigData.gcpAccountEmail)) ||
                            (cloudProvider === "aws" && cloudConfigData.awsCredentials.length === 0)
                        }
                    >
                        Save Configuration
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
